/** Opaque network failures some browsers surface as a generic message */
const OPAQUE_FETCH_ERRORS = new Set(["Load failed", "Failed to fetch"]);

// SENTRY FIX: VIDSEEDS-1Z4, VIDSEEDS-20X — Enhanced FS error messaging
// Date: 2026-04-16 | Agent: Cursor
// Root cause: The generic "temporary error, try again" message didn't guide users
//   to re-attach when their browser lost access to the video file. Many users
//   were retrying the same failed operation instead of re-attaching.
// Fix: Changed message to mention re-attaching as the recovery step. The error
//   is often triggered by: (1) transient FFmpeg state (retry works), (2) browser
//   losing the file handle (re-attach needed). Listing both options covers both cases.

/** Convert raw processing/FS errors to user-friendly messages */
export function humanizeProcessingError(
  err: unknown,
  fallback: string
): string {
  if (!(err instanceof Error)) return fallback;
  const msg = err.message;
  const name = err.name || "";

  // Abort signal fired (user cancelled or timeout signal)
  if (name === "AbortError" || msg.includes("aborted")) {
    return "The operation was cancelled. Please try again.";
  }

  // FFmpeg WASM filesystem errors
  if (
    name === "ErrnoError" ||
    msg.includes("FS error") ||
    msg.includes("ErrnoError")
  ) {
    return "Video processing encountered a temporary error. Try again, or if the issue persists, re-attach your video file.";
  }
  // Codec/format issues — also catch browser WebCodecs decode failures
  // SENTRY FIX: VIDSEEDS-29K, VIDSEEDS-2HS — MediaBunny codec decode error
  // Date: 2026-04-16 | Agent: Claude Code
  // Root cause: MediaBunny throws "cannot be decoded by this browser" for
  //   unsupported audio codecs. The existing codec/encoder check missed "decoded".
  // Fix: include "decoded" and "cannot be decoded" in the codec check.
  if (
    msg.includes("codec") ||
    msg.includes("encoder") ||
    msg.includes("decoded") ||
    msg.includes("cannot be decoded")
  ) {
    return "This video's audio format is not supported by your browser. Try converting to MP4 with AAC audio, or use a desktop browser.";
  }
  // Network timeouts — differentiate from generic timeouts
  if (msg.includes("timed out") || msg.includes("timeout")) {
    return "The request took too long. Please check your connection and try again.";
  }
  // Opaque network / fetch failures
  if (OPAQUE_FETCH_ERRORS.has(msg)) {
    return "A connection error occurred. Please check your network and try again.";
  }
  // Return original if short and readable, otherwise generic
  if (
    msg.length < 200 &&
    !msg.includes("ErrnoError") &&
    !msg.includes("FS error")
  ) {
    return msg;
  }
  return fallback;
}

/**
 * Classify the nature of a thumbnail generation error for UX purposes.
 * Returns a category that maps to specific translation keys.
 */
export function classifyThumbnailError(
  err: unknown
): "timeout" | "network" | "generic" {
  if (!(err instanceof Error)) return "generic";
  const msg = err.message;
  const name = err.name || "";

  if (
    name === "TimeoutError" ||
    name === "AbortError" ||
    msg.includes("timed out") ||
    msg.includes("timeout") ||
    msg.includes("aborted")
  ) {
    return "timeout";
  }

  if (
    OPAQUE_FETCH_ERRORS.has(msg) ||
    msg.includes("network") ||
    msg.includes("connection") ||
    msg.includes("Load failed") ||
    msg.includes("Failed to fetch")
  ) {
    return "network";
  }

  return "generic";
}
